import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Influencer } from '../types/influencer';
import type { CreatorCampaignData } from '../services/creatorPortalService';
import { getCreatorAuth, getCreatorProfile, getCreatorCampaigns } from '../services/creatorPortalService';
import { userService } from '../services/userService';
import type { RealtimeChannel } from '@supabase/supabase-js';

export function useCreatorPortalData() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsParam = searchParams.get('as');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [campaigns, setCampaigns] = useState<CreatorCampaignData[]>([]);
  const [viewingAs, setViewingAs] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [realtimeError, setRealtimeError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const influencerIdRef = useRef<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/creatorshub');
        return;
      }

      let influencerId: string | null = null;

      // Staff view-as mode: ?as=<influencer_id> lets admins/agents open a
      // creator's portal. Their own RLS grants the reads; the param only
      // selects which creator's data to assemble.
      if (viewAsParam) {
        const userRole = await userService.getCurrentUserRole();
        if (userRole && (userRole.role === 'admin' || userRole.role === 'agent')) {
          influencerId = viewAsParam;
          setViewingAs(true);
        }
      }

      if (!influencerId) {
        setViewingAs(false);
        const authData = await getCreatorAuth(user.id);
        if (!authData) throw new Error('Failed to load account information');
        if (authData.status === 'suspended') {
          await supabase.auth.signOut();
          navigate('/creatorshub');
          return;
        }
        influencerId = authData.influencer_id;
      }

      const influencerData = await getCreatorProfile(influencerId);
      setInfluencer(influencerData);
      influencerIdRef.current = influencerId;

      const campaignData = await getCreatorCampaigns(influencerId);
      setCampaigns(campaignData);
      setLastSyncedAt(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [navigate, viewAsParam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const infId = influencerIdRef.current;
    if (!infId) return;

    const channels: RealtimeChannel[] = [];
    let connectedCount = 0;
    const totalChannels = 4;

    const updateStatus = () => {
      setIsRealtimeConnected(connectedCount === totalChannels);
      if (connectedCount === totalChannels) setRealtimeError(null);
    };

    const onEvent = () => loadData();

    const subscribe = (name: string, table: string, filter?: string) => {
      const opts: any = { event: '*', schema: 'public', table };
      if (filter) opts.filter = filter;
      const ch = supabase
        .channel(name)
        .on('postgres_changes', opts, onEvent)
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') { connectedCount++; updateStatus(); }
          else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setRealtimeError('Real-time updates unavailable');
          }
        });
      channels.push(ch);
    };

    subscribe('creator-campaigns', 'campaigns');
    subscribe('creator-ci', 'campaign_influencers', `influencer_id=eq.${infId}`);
    subscribe('creator-deliverables', 'deliverables');
    subscribe('creator-invoices', 'invoices');

    return () => {
      setIsRealtimeConnected(false);
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, [influencer?.id, loadData]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/creatorshub');
  }, [navigate]);

  const isCampaignCompleted = (c: CreatorCampaignData) => {
    const s = c.status.toLowerCase();
    return s === 'liquidado' || s === 'completed' || s === 'paid';
  };

  const isCampaignConsultation = (c: CreatorCampaignData) => {
    const s = c.status.toLowerCase();
    return s === 'consulta' || s === 'consultation';
  };

  const activeCampaigns = campaigns.filter(c => !isCampaignCompleted(c) && !isCampaignConsultation(c));
  const completedCampaigns = campaigns.filter(c => isCampaignCompleted(c));
  const consultationCampaigns = campaigns.filter(c => isCampaignConsultation(c));
  const billingCampaigns = campaigns.filter(c => {
    const invoiceStatus = c.company_invoice_status;
    return invoiceStatus === 'Issued' || invoiceStatus === 'Paid' || invoiceStatus === 'Overdue';
  });

  const awaitingClientPaymentCampaigns = campaigns.filter(c => {
    return c.company_invoice_status === 'Issued' || c.company_invoice_status === 'Overdue';
  });

  const pipelineCampaigns = campaigns.filter(c => {
    if (isCampaignCompleted(c)) return false;
    return c.company_invoice_status === 'Not Issued';
  });

  return {
    loading,
    refreshing,
    error,
    viewingAs,
    influencer,
    campaigns,
    activeCampaigns,
    completedCampaigns,
    consultationCampaigns,
    billingCampaigns,
    awaitingClientPaymentCampaigns,
    pipelineCampaigns,
    isRealtimeConnected,
    realtimeError,
    lastSyncedAt,
    refresh,
    signOut,
    loadData,
  };
}
